/**
 * Iter 67en+++++++ — Duplicate booking warning dialog.
 *
 * Shown when the customer clicks "Book now" and the backend has detected
 * another active reservation with the same vendor whose dates overlap.
 * Soft-warning UX: user can either continue (which triggers the actual
 * checkout post they intended) or jump to their bookings dashboard to
 * review/cancel the conflicting one.
 *
 * Renders nothing when `overlapping` is empty so the parent can
 * always mount this component and toggle visibility via state.
 */
import { AlertTriangle, ArrowRight, X } from "lucide-react";
import { Link } from "react-router-dom";

function _fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
    });
  } catch { return iso; }
}

const STATUS_LABELS = {
  pending: "Pending",
  awaiting_payment: "Awaiting payment",
  confirmed: "Confirmed",
  in_progress: "In progress",
  checked_out: "Picked up",
  active: "Active",
};

export default function DuplicateBookingDialog({
  open,
  overlapping = [],
  onClose,
  onContinue,
}) {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-neutral-900/60 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      data-testid="duplicate-booking-dialog"
    >
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden border border-neutral-200">
        <div className="flex items-start justify-between gap-3 px-5 pt-5">
          <div className="flex items-start gap-3 min-w-0">
            <div className="h-10 w-10 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-display text-lg font-semibold text-neutral-900">
                You already have a booking for these dates
              </h3>
              <p className="text-sm text-neutral-600 mt-1 leading-relaxed">
                You've already reserved a car with this host for an
                overlapping date range. If you continue, you'll end up
                with two active bookings.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-neutral-400 hover:text-neutral-700 transition shrink-0 -mr-1 -mt-1"
            data-testid="duplicate-booking-close"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <ul className="px-5 pt-4 space-y-2" data-testid="duplicate-booking-list">
          {overlapping.slice(0, 3).map((b) => (
            <li
              key={b.id}
              className="rounded-2xl border border-amber-200 bg-amber-50/60 px-3 py-2.5 text-sm"
              data-testid={`duplicate-booking-row-${b.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-neutral-900 truncate">{b.car_name || "Booking"}</div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-amber-800 shrink-0">
                  {STATUS_LABELS[b.status] || b.status}
                </span>
              </div>
              <div className="text-xs text-neutral-600 mt-0.5">
                {_fmtDate(b.start_date)} → {_fmtDate(b.end_date)}
                {b.vendor_name ? ` · ${b.vendor_name}` : ""}
              </div>
            </li>
          ))}
          {overlapping.length > 3 && (
            <li className="text-xs text-neutral-500 px-1">
              + {overlapping.length - 3} more overlapping booking{overlapping.length - 3 === 1 ? "" : "s"}
            </li>
          )}
        </ul>

        <div className="px-5 pt-5 pb-5 flex flex-col-reverse sm:flex-row gap-2 sm:gap-2 sm:justify-end">
          <Link
            to="/dashboard/customer?tab=bookings"
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-700 text-sm font-semibold transition"
            data-testid="duplicate-booking-view-bookings"
            onClick={onClose}
          >
            View my bookings <ArrowRight className="h-3.5 w-3.5" />
          </Link>
          <button
            type="button"
            onClick={onContinue}
            className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition"
            data-testid="duplicate-booking-continue"
          >
            Book anyway
          </button>
        </div>
      </div>
    </div>
  );
}
